import React, { useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VinHint } from "@/components/VinHint";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";

// Common vehicle brand list surfaced as a datalist so the operator can pick
// the logo with one click but still type a custom brand if we ever get a
// truck / RV / motorcycle order. Kept intentionally short — only the ones
// Leprecon+ actually stamps logos for. Extend as new brand jigs arrive.
const LOGO_BRANDS = [
  "Acura", "Alfa Romeo", "Audi", "Bentley", "BMW", "Buick", "Cadillac",
  "Chevrolet", "Chrysler", "Dodge", "Ferrari", "Fiat", "Ford", "Genesis",
  "GMC", "Honda", "Hyundai", "Infiniti", "Jaguar", "Jeep", "Kia",
  "Lamborghini", "Land Rover", "Lexus", "Lincoln", "Lucid", "Maserati",
  "Mazda", "McLaren", "Mercedes-Benz", "MINI", "Mitsubishi", "Nissan",
  "Polestar", "Porsche", "Ram", "Rivian", "Rolls-Royce", "Subaru", "Tesla",
  "Toyota", "Volkswagen", "Volvo",
];

// "Manual Pre Order" dialog. Handles two flows:
//   • Pick an EXISTING customer via keyboard-searchable combobox
//   • Create a NEW customer inline (name / phone / email / address) — no need
//     to leave the form; on Save we POST /customers then POST /orders in
//     sequence so both are created in one click.
//
// `mode` prop:
//   • "pre_order" (default) — creates a regular Pre Order, PO number required
//     and typed by the operator, POST /orders.
//   • "lead" — creates a Lead. VIN is required and its last 6 chars are
//     shown as a live LD-XXXXXX preview. PO number field is hidden — the
//     backend derives po_number = LD-{last6VIN}. POST /leads.
export default function ManualPreOrderDialog({ triggerLabel = "Manual Pre Order", triggerClassName, onCreated, mode: uiMode = "pre_order" }) {
  const isLead = uiMode === "lead";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("existing"); // 'existing' | 'new'

  const { data: customers = [], isLoading: custLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await api.get("/customers")).data,
    enabled: open,
  });

  // Growing pool of every logo/brand ever used on an order (Shopify + manual).
  // Merged with the built-in LOGO_BRANDS baseline so brand-new stores still
  // get a useful starter list.
  const { data: savedLogos = [] } = useQuery({
    queryKey: ["logo-suggestions"],
    queryFn: async () => (await api.get("/logo-suggestions")).data,
    enabled: open,
  });
  const logoOptions = useMemo(() => {
    const set = new Set();
    for (const b of LOGO_BRANDS) set.add(b);
    for (const s of savedLogos) if (s && typeof s === "string") set.add(s.trim());
    return Array.from(set).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [savedLogos]);

  // Existing-customer picker: combobox with keyboard filter
  const [pickQuery, setPickQuery] = useState("");
  const [pickedId, setPickedId] = useState("");
  const filtered = useMemo(() => {
    const q = pickQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers
      .filter((c) => {
        const hay = `${c.name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [pickQuery, customers]);

  // New-customer inline fields
  const emptyNew = { name: "", email: "", phone: "", address: "" };
  const [newCust, setNewCust] = useState(emptyNew);

  // Common order fields
  const emptyOrder = { po_number: "", vin: "", color: "", logo: "", is_pickup: false };
  const [order, setOrder] = useState(emptyOrder);

  const create = useMutation({
    mutationFn: async () => {
      // Step 1 — resolve or create the customer
      let customer_id = pickedId;
      if (mode === "new") {
        if (!newCust.name.trim()) throw new Error("Client name is required");
        const r = await api.post("/customers", {
          name: newCust.name.trim(),
          email: newCust.email.trim() || null,
          phone: newCust.phone.trim() || null,
          address: newCust.address.trim() || null,
        });
        customer_id = r.data.id;
      } else {
        if (!customer_id) throw new Error("Pick a client (or switch to New Client)");
      }
      // Step 2 — create the order (Lead vs regular Pre Order)
      if (isLead) {
        const vin = (order.vin || "").trim().toUpperCase();
        if (!vin) throw new Error("VIN is required for a Lead (last 6 chars become the LD number)");
        if (vin.length < 6) throw new Error("VIN too short — need at least 6 characters");
        const leadPayload = {
          customer_id,
          vin,
          color: order.color.trim() || null,
          logo: order.logo.trim() || null,
        };
        const o = await api.post("/leads", leadPayload);
        // Fire-and-forget Drive sync (photos matching the LD number will get linked).
        api.post("/drive/sync").catch(() => {});
        return o.data;
      }
      if (!order.po_number.trim()) throw new Error("PO Number is required");
      const orderPayload = {
        customer_id,
        po_number: order.po_number.trim(),
        vin: order.vin.trim() || null,
        color: order.color.trim() || null,
        is_pickup: !!order.is_pickup,
      };
      const o = await api.post("/orders", orderPayload);
      // Step 3 — patch the logo (POST /orders doesn't accept `logo` directly)
      if (order.logo.trim()) {
        try {
          await api.patch(`/orders/${o.data.id}`, { logo: order.logo.trim() });
        } catch (_) { /* logo is optional — don't block success */ }
      }
      // Step 4 — kick off a Drive sync so any photos already sitting in the
      // root folder get linked to this order right away. Fire-and-forget:
      // Drive sync can take a few seconds, we don't want to block the toast.
      api.post("/drive/sync").catch(() => { /* sync is best-effort */ });
      return o.data;
    },
    onSuccess: (o) => {
      toast.success(`${isLead ? "Lead" : "Pre Order"} created · ${o.po_number}`);
      qc.invalidateQueries();
      resetAndClose();
      if (typeof onCreated === "function") { try { onCreated(o); } catch (_) {} }
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || e?.message || `Failed to create ${isLead ? "Lead" : "order"}`);
    },
  });

  const resetAndClose = () => {
    setOpen(false);
    setMode("existing");
    setPickQuery("");
    setPickedId("");
    setNewCust(emptyNew);
    setOrder(emptyOrder);
  };

  const canSubmit = !create.isPending && (
    (mode === "existing" && pickedId) ||
    (mode === "new" && newCust.name.trim())
  ) && (isLead
    ? (order.vin || "").trim().length >= 6
    : !!order.po_number.trim()
  );

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : resetAndClose())}>
      <DialogTrigger asChild>
        <Button
          data-testid={isLead ? "new-lead-btn" : "manual-preorder-btn"}
          className={triggerClassName || (isLead ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white")}
        >
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl" data-testid={isLead ? "new-lead-dialog" : "manual-preorder-dialog"}>
        <DialogHeader>
          <DialogTitle>{isLead ? "Manual Lead" : "Manual Pre Order"}</DialogTitle>
          <DialogDescription>
            {isLead
              ? "Create a Lead — a prospective order without a paid Shopify PO yet. The last 6 chars of the VIN become the LD number. Back Office / Super Admin converts the LD to a real PO in Advance Orders."
              : "Create a Pre Order manually. Pick an existing client (type to search) or add a new one on the fly. Photos uploaded to the root Drive folder will auto-link on save."}
          </DialogDescription>
        </DialogHeader>

        {/* Mode switch */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <button
            type="button"
            data-testid="mp-mode-existing"
            onClick={() => setMode("existing")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-md ${
              mode === "existing" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            👤 Existing Client
          </button>
          <button
            type="button"
            data-testid="mp-mode-new"
            onClick={() => setMode("new")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-md ${
              mode === "new" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            ➕ New Client
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
          {/* LEFT: client picker */}
          <div>
            {mode === "existing" ? (
              <CustomerCombobox
                customers={filtered}
                query={pickQuery}
                setQuery={setPickQuery}
                picked={pickedId}
                setPicked={setPickedId}
                loading={custLoading}
                totalCount={customers.length}
              />
            ) : (
              <div className="rounded-md border-2 border-slate-900/10 bg-slate-50 p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1">
                  New Client details
                </div>
                <div>
                  <Label className="text-xs">Full Name <span className="text-red-600">*</span></Label>
                  <Input
                    data-testid="mp-new-name"
                    value={newCust.name}
                    onChange={(e) => setNewCust({ ...newCust, name: e.target.value })}
                    placeholder="e.g. Jane Doe"
                    autoFocus
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      data-testid="mp-new-phone"
                      value={newCust.phone}
                      onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })}
                      placeholder="+1 555 123 4567"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      data-testid="mp-new-email"
                      value={newCust.email}
                      onChange={(e) => setNewCust({ ...newCust, email: e.target.value })}
                      placeholder="client@example.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Address</Label>
                  <Input
                    data-testid="mp-new-address"
                    value={newCust.address}
                    onChange={(e) => setNewCust({ ...newCust, address: e.target.value })}
                    placeholder="Street, City, State, ZIP, Country"
                  />
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: order details */}
          <div className="rounded-md border-2 border-emerald-900/10 bg-emerald-50/40 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-800 mb-1">
              {isLead ? "Lead details" : "Order details"}
            </div>
            {!isLead && (
              <div>
                <Label className="text-xs">PO Number <span className="text-red-600">*</span></Label>
                <Input
                  data-testid="mp-order-po"
                  value={order.po_number}
                  onChange={(e) => setOrder({ ...order, po_number: e.target.value })}
                  placeholder="e.g. 4744 (from Shopify) or your own numbering"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">
                VIN {isLead ? <span className="text-red-600">*</span> : <span className="text-slate-400">(optional)</span>}
              </Label>
              <Input
                data-testid="mp-order-vin"
                value={order.vin}
                onChange={(e) => {
                  // VIN sanitation: uppercase + fix look-alike OCR mistakes
                  // (letters O/I/Q are ILLEGAL in a real VIN — the operator
                  // almost always means 0/1/0). Business rule Sep 2, 2026.
                  const clean = e.target.value
                    .toUpperCase()
                    .replace(/O/g, "0")
                    .replace(/I/g, "1")
                    .replace(/Q/g, "0");
                  setOrder({ ...order, vin: clean });
                }}
                placeholder="17-char VIN"
                className="font-mono uppercase vin-glyphs"
              />
              <VinHint value={order.vin} testid="mp-vin-hint" />
              {isLead && (
                <div className="text-[11px] font-mono mt-1">
                  {(order.vin || "").trim().length >= 6 ? (
                    <span className="text-emerald-700">→ LD-{(order.vin || "").trim().toUpperCase().slice(-6)}</span>
                  ) : (
                    <span className="text-slate-500">Enter at least 6 chars → LD-XXXXXX</span>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Color</Label>
                <Input
                  data-testid="mp-order-color"
                  value={order.color}
                  onChange={(e) => setOrder({ ...order, color: e.target.value })}
                  placeholder="e.g. Red"
                />
              </div>
              <div>
                <Label className="text-xs">Logo <span className="text-slate-400">(brand)</span></Label>
                <LogoCombobox
                  value={order.logo}
                  onChange={(v) => setOrder({ ...order, logo: v })}
                  options={logoOptions}
                />
              </div>
            </div>
            {!isLead && (
              <button
                type="button"
                data-testid="mp-order-pickup-toggle"
                aria-pressed={!!order.is_pickup}
                onClick={() => setOrder({ ...order, is_pickup: !order.is_pickup })}
                className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  order.is_pickup ? "border-violet-400 bg-violet-50 text-violet-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="font-semibold">🏬 Pick up {order.is_pickup ? "— ON" : ""}</span>
                <span className="text-[11px] text-slate-500">
                  {order.is_pickup ? "Client collects at the shop · no shipping" : "Click if the client will pick up at the shop"}
                </span>
              </button>
            )}
            <div className="text-[11px] text-slate-600 pt-2 border-t border-emerald-900/10 mt-2 leading-relaxed">
              📁 Once you save, we'll auto-sync the Drive root folder so any
              photos matching this {isLead ? "LD number" : "PO number"} get linked to the order.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={resetAndClose}
            disabled={create.isPending}
            data-testid="mp-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!canSubmit}
            className={isLead ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}
            data-testid="mp-submit"
          >
            {create.isPending
              ? "Creating…"
              : isLead
                ? (mode === "new" ? "Create Client + Save Lead" : "Save Lead")
                : mode === "new"
                  ? "Create Client + Save Order"
                  : "Save Pre Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Searchable logo dropdown. Type to filter, click to pick from the list,
// or just leave whatever the operator typed as a brand-new logo — that
// entry gets remembered next time /logo-suggestions is queried.
function LogoCombobox({ value, onChange, options }) {
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const q = (value || "").trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.toLowerCase().includes(q)).slice(0, 40)
    : options.slice(0, 40);
  const exact = q && options.some((o) => o.toLowerCase() === q);

  return (
    <div ref={boxRef} className="relative">
      <Input
        data-testid="mp-order-logo"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Type to search or add — Tesla, BMW, Ferrari…"
      />
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg" data-testid="mp-logo-list">
          {filtered.length === 0 && !q && (
            <div className="p-3 text-center text-xs text-slate-400">No logos saved yet — type one and we'll remember it.</div>
          )}
          {q && !exact && (
            <button
              type="button"
              onClick={() => { onChange(value.trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-emerald-50 flex items-center justify-between"
              data-testid="mp-logo-use-typed"
            >
              <span className="text-sm">Use "<b>{value.trim()}</b>" as a new logo</span>
              <span className="text-[10px] uppercase tracking-widest font-mono text-emerald-700">+ new</span>
            </button>
          )}
          {filtered.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 hover:bg-slate-50 text-sm ${
                value === opt ? "bg-slate-900 text-white hover:bg-slate-900" : ""
              }`}
              data-testid={`mp-logo-opt-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Keyboard-searchable customer picker. Simple, no external dep — a filter
// input above a scrollable list. Highlights the selected row.
function CustomerCombobox({ customers, query, setQuery, picked, setPicked, loading, totalCount }) {
  const inputRef = useRef(null);
  React.useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="space-y-2 h-full flex flex-col">
      <div className="sticky top-0 bg-white z-10 pb-2 border-b border-slate-100">
        <Label className="text-xs">🔍 Search clients (name, email, or phone)</Label>
        <Input
          ref={inputRef}
          data-testid="mp-customer-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type to filter — e.g. Carl or 555…"
          className="font-mono h-11 text-base border-slate-400 focus:border-slate-900"
        />
        <div className="text-[10px] text-slate-500 mt-1 font-mono">
          {loading ? "Loading clients…" : `Showing ${customers.length} of ${totalCount}${query ? ` for "${query}"` : ""}`}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100 min-h-[300px]" data-testid="mp-customer-list">
        {customers.length === 0 && (
          <div className="p-4 text-center text-sm text-slate-400">
            No clients match — try a different search or switch to <b>New Client</b>.
          </div>
        )}
        {customers.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setPicked(c.id)}
            data-testid={`mp-customer-${c.id}`}
            className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${
              picked === c.id ? "bg-slate-900 text-white hover:bg-slate-900" : ""
            }`}
          >
            <div className="text-sm font-semibold">{c.name || "(no name)"}</div>
            <div className={`text-xs font-mono ${picked === c.id ? "text-slate-300" : "text-slate-500"}`}>
              {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
